import NotificationModule from "./notification_module";
class EllipseGradingModule {
  constructor(config) {
    this.controller = config.controller;
    this.iolGradingApi = config.iolGradingApi;
    this.saveUrl = config.saveUrl;
    this.method = config.method;
    this.minPoints = config.minPoints || 10;
    this.iolDiameter = config.iolDiameter;
    this.ellipseData = config.ellipseData || null;
    this.cornerstone = config.cornerstone; // Store cornerstone dependency
    this.ellipseRenderer = null;
    this.visibilityStates = config.visibilityStates;
    this.activeTypes = config.activeTypes;
    const loaderElem = document.getElementById("loader"); // or any selector
    this.notifications = new NotificationModule(loaderElem);
    this.gradingEllipseData = config.gradingEllipseData || null;
    this.users = config.users;
  }

  designEllipse(annotations, imageUrl = null) {
    return new Promise((resolve, reject) => {
      // Validate imageUrl
      if (!imageUrl || typeof imageUrl !== "string" || imageUrl.trim() === "") {
        alert("Invalid image URL provided.");
        reject(new Error("Invalid image URL"));
        return;
      }

      // Get the annotation for this imageUrl

      const imageAnn = annotations[imageUrl];
      if (!imageAnn) {
        alert("No annotation data found for this image URL.");
        reject(new Error("Annotation not found for image URL"));
        return;
      }

      // Optional: preload the image
      const img = new Image();
      img.onload = () => {
        const result = {
          IOL: [],
          Pupil: [],
          Cornea: [],
          IOL_diameter: this.iolDiameter,
        };

        if (imageAnn?.IOLProbe?.data) {
          imageAnn.IOLProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.IOL.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        if (imageAnn?.PupilProbe?.data) {
          imageAnn.PupilProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.Pupil.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        if (imageAnn?.CorneaProbe?.data) {
          imageAnn.CorneaProbe.data.forEach((m) => {
            if (m.handles?.end) {
              result.Cornea.push([
                Math.round(m.handles.end.x),
                Math.round(m.handles.end.y),
              ]);
            }
          });
        }

        const insufficient = [];
        if (result.IOL.length < this.minPoints) insufficient.push("IOL");
        if (result.Pupil.length < this.minPoints) insufficient.push("Pupil");
        if (result.Cornea.length < this.minPoints) insufficient.push("Cornea");

        if (insufficient.length > 0) {
          alert(
            `Please add at least ${
              this.minPoints
            } points for: ${insufficient.join(", ")}`
          );
          this.notifications.hideLoader();
          // reject(new Error("Insufficient probe points"));
          return;
        }

        this.getIolDecentration(result).then(resolve).catch(reject);
      };

      img.onerror = () => {
        alert("Failed to load the image from the provided URL.");
        reject(new Error("Image load error"));
      };

      // Start loading the image
      img.src = imageUrl;
    });
  }

  renderEllipses(event) {
    let ellipseDataToUse = null;
    
    // Check if users selector exists (adjudication view) or not (grading view)
    if (this.users && this.users.value) {
      const selectedUser = this.users.value;
      
      // Decide which data to use based on selected user
      if (selectedUser === "adjudicator") {
        // Use adjudicator's ellipse data
        if (this.ellipseData && Object.keys(this.ellipseData).length > 0) {
          ellipseDataToUse = this.ellipseData;
        } else {
          return;
        }
      } else {
        // Use grader's ellipse data
        if (this.gradingEllipseData && 
            this.gradingEllipseData[selectedUser] && 
            Object.keys(this.gradingEllipseData[selectedUser]).length > 0) {
          ellipseDataToUse = this.gradingEllipseData[selectedUser];
        } else {
          return;
        }
      }
    } else {
      // No user selector (grading view) - use current grader's ellipse data
      if (this.ellipseData && Object.keys(this.ellipseData).length > 0) {
        ellipseDataToUse = this.ellipseData;
      } else {
        return;
      }
    }
    
    // Get current image URL
    const currentUrl = this.controller.url;
    
    // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
    const getFilename = (url) => {
      const parts = url.split('/');
      return parts[parts.length - 1]; // Get last part (filename)
    };
    
    const currentFilename = getFilename(currentUrl);
    
    // Get ellipse data specific to this image - try exact match first, then match by filename
    let currentImageData = ellipseDataToUse[currentUrl];
    
    if (!currentImageData) {
      // Try matching by filename since ActiveStorage tokens change
      for (const ellipseUrl in ellipseDataToUse) {
        const ellipseFilename = getFilename(ellipseUrl);
        if (ellipseFilename === currentFilename) {
          currentImageData = ellipseDataToUse[ellipseUrl];
          break;
        }
      }
    }

    // If no data for this image, don't draw anything
    if (!currentImageData || currentImageData.length === 0) {
      return;
    }

    const { element, canvasContext: context, enabledElement } = event.detail;
    context.save();
    this.cornerstone.setToPixelCoordinateSystem(enabledElement, context);

    // Draw using the data for current image
    this.drawAllEllipses(context, currentImageData[0], enabledElement);

    context.restore();
  }

  drawAllEllipses(context, data, enabledElement = null) {
    const ellipseConfig = {
      Cornea: { color: "#FF0000", label: "Cornea" },
      IOL: { color: "#00FF00", label: "IOL" },
      Pupil: { color: "#0000FF", label: "Pupil" },
    };
    const scaleFactor = 2;
    const centers = {};

    // Calculate a scale factor based on the actual image dimensions to make line widths responsive
    // This ensures consistent line thickness relative to the image content, regardless of zoom
    let canvasScale = 1;
    if (enabledElement && enabledElement.image) {
      // Use actual image dimensions - scale relative to a baseline image size
      const baselineImageSize = 2000;
      const imageSize = Math.max(enabledElement.image.width, enabledElement.image.height);
      canvasScale = imageSize / baselineImageSize;
    } else if (enabledElement && enabledElement.viewport && enabledElement.viewport.scale) {
      // Fallback to viewport scale
      canvasScale = enabledElement.viewport.scale;
    } else {
      // Final fallback to canvas size-based scaling
      const baselineCanvasSize = 2000;
      canvasScale = Math.max(context.canvas.width, context.canvas.height) / baselineCanvasSize;
    }
    
    // Define base sizes that will be scaled
    const baseLineWidth = 3;
    const baseThinLineWidth = 1;
    const baseCenterPointRadius = 3;
    const baseLabelFontSize = 14;
    const baseDistanceFontSize = 36;
    const baseLabelOffset = 5;
    const baseDistanceOffset = 500;
    
    // Apply scale to all sizes
    const ellipseLineWidth = baseLineWidth * canvasScale;
    const axisLineWidth = baseThinLineWidth * canvasScale;
    const centerPointLineWidth = baseThinLineWidth * canvasScale;
    const centerPointRadius = baseCenterPointRadius * canvasScale;
    const labelFontSize = baseLabelFontSize * canvasScale;
    const distanceFontSize = baseDistanceFontSize * canvasScale;
    const labelOffset = baseLabelOffset * canvasScale;
    const distanceOffset = baseDistanceOffset * canvasScale;
    const dashPattern = [2 * canvasScale, 2 * canvasScale];
    const iolPupilDashPattern = [
      10 * canvasScale, 
      2 * canvasScale, 
      2 * canvasScale, 
      2 * canvasScale
    ];

    Object.entries(ellipseConfig).forEach(([key, { color, label }]) => {
      const params = data[key]?.params;
      if (!params?.center_x || !params?.center_y) return;

      const cx = params.center_x;
      const cy = params.center_y;
      const rx = (params.major_axis / 2) * scaleFactor;
      const ry = (params.minor_axis / 2) * scaleFactor;
      const rotation = ((params.major_axis_angle || 0) * Math.PI) / 180;

      centers[key] = [cx, cy];
      const visibilityState = this.visibilityStates[key.toLowerCase()];

      if (visibilityState === "contour" || visibilityState === "both") {
        // Draw ellipse contour
        context.setLineDash([]);
        context.beginPath();
        context.ellipse(cx, cy, rx, ry, rotation, 0, 2 * Math.PI);
        context.strokeStyle = color;
        context.lineWidth = ellipseLineWidth;
        context.stroke();

        // Draw axis lines (major and minor)
        const axis = data[key]?.axis_endpoints;
        if (axis) {
          // Major axis
          if (axis.major_start && axis.major_end) {
            context.beginPath();
            context.moveTo(axis.major_start[0], axis.major_start[1]);
            context.lineTo(axis.major_end[0], axis.major_end[1]);
            context.strokeStyle = color;
            context.lineWidth = axisLineWidth;
            context.setLineDash(dashPattern);
            context.stroke();
          }

          // Minor axis
          if (axis.minor_start && axis.minor_end) {
            context.beginPath();
            context.moveTo(axis.minor_start[0], axis.minor_start[1]);
            context.lineTo(axis.minor_end[0], axis.minor_end[1]);
            context.strokeStyle = color;
            context.lineWidth = axisLineWidth;
            context.setLineDash(dashPattern);
            context.stroke();
          }
        }

        // Always draw center point
        context.setLineDash([]);
        context.beginPath();
        context.arc(cx, cy, centerPointRadius, 0, 2 * Math.PI);
        context.strokeStyle = color;
        context.lineWidth = centerPointLineWidth;
        context.stroke();

        // Always draw label
        context.font = `bold ${labelFontSize}px Arial`;
        context.textAlign = "center";
        context.fillStyle = color;
        context.fillText(label, cx, cy - ry - labelOffset);
      }
    });

    //  Draw dotted line from IOL to Pupil only if both are visible
    const iolVisible = ["contour", "both"].includes(this.visibilityStates.iol);
    const pupilVisible = ["contour", "both"].includes(
      this.visibilityStates.pupil
    );

    if (iolVisible && pupilVisible && centers.IOL && centers.Pupil) {
      const [iolX, iolY] = centers.IOL;
      const [pupilX, pupilY] = centers.Pupil;

      context.beginPath();
      context.moveTo(iolX, iolY);
      context.lineTo(pupilX, pupilY);
      context.strokeStyle = "#17A2B8";
      context.lineWidth = axisLineWidth;
      context.setLineDash(iolPupilDashPattern);
      context.stroke();

      // Round distance to 2 decimal places using standard rounding (0-4 down, 5-9 up)
      let distance = data.IOL_Pupil_Distance;
      if (distance != null && typeof distance === "number") {
        // First round the number, then format to 2 decimal places
        distance = (Math.round(distance * 100) / 100).toFixed(2);
      } else if (distance == null) {
        distance = "N/A";
      }
      
      // Round angle to nearest integer (no decimals)
      let angle = data.IOL_Pupil_Decentration_Angle_beta;
      if (angle != null && typeof angle === "number") {
        angle = Math.round(angle);
      }

      // Display IOL-Pupil distance and angle when Cornea is visible (for positioning reference)
      // These values are controlled by Pupil visibility since they represent IOL-Pupil relationship
      if (centers.Cornea) {
        const [corneaX, corneaY] = centers.Cornea;

        context.font = `bold ${distanceFontSize}px Arial`;
        context.fillStyle = "green";
        context.textAlign = "right";
        context.setLineDash([]);

        // Display decentration distance
        context.fillText(`${distance} mm`, corneaX - distanceOffset, corneaY);
        
        // Display angle below decentration if available
        if (angle !== null) {
          const angleYOffset = distanceFontSize + (10 * canvasScale); // Space between lines
          context.fillText(`${angle} deg`, corneaX - distanceOffset, corneaY + angleYOffset);
        }
      }
    }
  }

  async getIolDecentration(data) {
    try {
      const response = await fetch(`${this.iolGradingApi}/IOL-Decentration`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content"),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      // Initialize ellipseData if not already
      this.ellipseData = this.ellipseData || {};
      // Get the current image URL
      const currentUrl = this.controller.url;

      // Always replace data for the same URL, or create new entry for different URLs
      this.ellipseData[currentUrl] = [result];

      this.addEllipseRenderer();

      // Return only the current URL's data
      return this.ellipseData;
    } catch (error) {
      console.error("IOL Decentration API Error:", error);
      // Consider returning null or throwing the error depending on your error handling strategy
      return null;
    }
  }

  addEllipseRenderer() {
    const elements = this.cornerstone.getEnabledElements();
    if (!elements.length) return;

    this.ellipseRenderer =
      this.ellipseRenderer || this.renderEllipses.bind(this);

    elements.forEach(({ element }) => {
      element.removeEventListener(
        "cornerstoneimagerendered",
        this.ellipseRenderer
      );
      element.addEventListener(
        "cornerstoneimagerendered",
        this.ellipseRenderer
      );

      if (this.cornerstone.getImage(element)) {
        this.cornerstone.updateImage(element);
      }
    });
  }

  setVisibility(type) {
    type = type.toLowerCase();
    // Validate that it's a recognized type
    if (!["iol", "pupil", "cornea"].includes(type)) {
      console.error(`Invalid ellipse type: ${type}`);
      return;
    }
    // Cycle through visibility states: both -> contour -> points -> none -> both
    const states = ["both", "contour", "points", "none"];
    const currentIndex = states.indexOf(this.visibilityStates[type]);
    const nextIndex = (currentIndex + 1) % states.length;
    this.visibilityStates[type] = states[nextIndex];
    // Re-render with the new visibility state
    this.addEllipseRenderer();

    return this.visibilityStates[type];
  }
}

// Export the class for use in other modules
export default EllipseGradingModule;
